import type { useQuery } from "convex/react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { formatRelativeTime } from "@/lib/time";
import { cn } from "@/lib/utils";
import { AgentDetailPanel } from "@/modules/threads/AgentDetailPanel";
import { LiveThreadMessages } from "@/modules/threads/LiveThreadMessages";
import type { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";

type ScenarioGet = NonNullable<
	ReturnType<typeof useQuery<typeof api.scenarios.get>>
>;
type ScenarioRun = ScenarioGet["recentRuns"][number];
type ScenarioRunStatus = ScenarioRun["status"];
type ScenarioTurn = ScenarioRun["turns"][number];
type ScenarioTurnRole = ScenarioTurn["role"];

export function RunsPanel({
	runs,
	selectedRunId,
	onSelectRun,
}: {
	runs: ScenarioRun[];
	selectedRunId: Id<"scenarioRuns"> | null;
	onSelectRun: (id: Id<"scenarioRuns">) => void;
}) {
	const selectedRun = runs.find((r) => r._id === selectedRunId) ?? null;

	return (
		<div className="flex h-full min-h-0">
			<div className="flex w-72 shrink-0 flex-col overflow-hidden">
				<ScrollArea className="h-full min-h-0 flex-1">
					{runs.length === 0 ? (
						<div className="p-6 text-center text-sm text-muted-foreground">
							No runs yet. Click "Run scenario" to start one.
						</div>
					) : (
						<div className="flex flex-col">
							{runs.map((run) => (
								<RunListRow
									key={run._id}
									run={run}
									selected={run._id === selectedRunId}
									onSelect={() => onSelectRun(run._id)}
								/>
							))}
						</div>
					)}
				</ScrollArea>
			</div>

			<Separator orientation="vertical" className="h-auto" />

			<div className="flex min-w-0 flex-1 flex-col overflow-hidden">
				{selectedRun ? (
					<LiveThreadMessages clericalThreadId={selectedRun.clericalThreadId} />
				) : (
					<div className="flex flex-1 items-center justify-center p-6 text-sm text-muted-foreground">
						Select a run to inspect.
					</div>
				)}
			</div>

			<Separator orientation="vertical" className="h-auto" />

			<div className="flex w-96 shrink-0 flex-col overflow-hidden">
				{selectedRun ? (
					<AgentDetailPanel clericalThreadId={selectedRun.clericalThreadId} />
				) : (
					<div className="flex h-full items-center justify-center p-6 text-sm text-muted-foreground">
						No run selected.
					</div>
				)}
			</div>
		</div>
	);
}

function RunListRow({
	run,
	selected,
	onSelect,
}: {
	run: ScenarioRun;
	selected: boolean;
	onSelect: () => void;
}) {
	const elapsed = run.completedAt
		? `${Math.round((run.completedAt - run.startedAt) / 1000)}s`
		: "—";
	const terminalTurn = run.turns.find((t) => t.terminationReason);

	return (
		<Button
			type="button"
			variant="ghost"
			onClick={onSelect}
			className={cn(
				"flex h-auto w-full flex-col items-stretch justify-start gap-1 whitespace-normal rounded-none border-b px-3 py-2.5 text-left font-normal",
				selected && "bg-muted",
			)}
		>
			<div className="flex items-center gap-2">
				<RunStatusBadge status={run.status} />
				<span className="truncate text-sm font-medium">
					Turn {run.turnCount}/{run.maxTurns}
				</span>
				<span className="ml-auto shrink-0 text-[11px] text-muted-foreground">
					{run.turns.length} events
				</span>
			</div>
			<div className="flex items-center gap-2 text-[11px] text-muted-foreground">
				<span>Started {formatRelativeTime(run.startedAt)}</span>
				<span>·</span>
				<span>Elapsed {elapsed}</span>
			</div>
			<TurnsStrip turns={run.turns} />
			{terminalTurn?.terminationReason ? (
				<p className="truncate text-[11px] text-muted-foreground">
					{terminalTurn.terminationReason}
				</p>
			) : null}
			{run.errorMessage ? (
				<p className="truncate text-[11px] text-destructive">
					{run.errorMessage}
				</p>
			) : null}
		</Button>
	);
}

function TurnsStrip({ turns }: { turns: ScenarioTurn[] }) {
	if (turns.length === 0) return null;
	return (
		<div className="flex flex-wrap gap-1">
			{turns.map((t) => (
				<span
					key={`${t.role}-${t.startedAt}`}
					className={cn(
						"rounded-sm px-1 py-[1px] text-[9px] font-medium uppercase",
						ROLE_STYLE[t.role],
					)}
					title={tooltipText(t)}
				>
					{ROLE_LABEL[t.role]}
				</span>
			))}
		</div>
	);
}

function tooltipText(t: ScenarioTurn): string {
	if (t.terminationReason) return t.terminationReason;
	if (t.error) return t.error;
	if (!t.body) return t.role;
	return t.body.length > 240 ? `${t.body.slice(0, 240)}…` : t.body;
}

const ROLE_LABEL: Record<ScenarioTurnRole, string> = {
	trigger: "Trigger",
	persona: "Persona",
	drafter: "Drafter",
};

const ROLE_STYLE: Record<ScenarioTurnRole, string> = {
	trigger: "bg-warning/20 text-warning",
	persona: "bg-primary/20 text-primary",
	drafter: "bg-success/20 text-success",
};

export function RunStatusBadge({ status }: { status: ScenarioRunStatus }) {
	const map = {
		running: { label: "Running", variant: "default" as const },
		completed: { label: "Completed", variant: "secondary" as const },
		failed: { label: "Failed", variant: "destructive" as const },
		aborted: { label: "Aborted", variant: "outline" as const },
	};
	const cfg = map[status];
	return <Badge variant={cfg.variant}>{cfg.label}</Badge>;
}
